import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const MAPUTO_TIMEZONE = 'Africa/Harare';

function parseBackendDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  if (!raw) return null;

  const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(raw);
  const isoBase = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const normalized = hasTimezone ? isoBase : `${isoBase}Z`;

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat('pt-MZ', {
    style: 'currency',
    currency: 'MZN'
  }).format(amount || 0);
}

export function formatDate(date) {
  const parsedDate = parseBackendDate(date);
  if (!parsedDate) return '';

  return parsedDate.toLocaleDateString('pt-MZ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: MAPUTO_TIMEZONE
  });
}

export function formatDateTime(date) {
  const parsedDate = parseBackendDate(date);
  if (!parsedDate) return '';

  return parsedDate.toLocaleString('pt-MZ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: MAPUTO_TIMEZONE
  });
}

const CODE_PREFIX_ALIASES = {
  SALE: 'SAL',
  SAL: 'SAL',
  SRV: 'SRV',
  WAR: 'WAR',
  GAM: 'GAM',
  PAP: 'PAP',
  OTH: 'OTH',
  PRJ: 'PRJ',
  INV: 'INV',
  FAC: 'FAC',
  VD: 'VD',
  REC: 'REC',
  COT: 'COT',
  GE: 'GE',
  GR: 'GR',
  NC: 'NC',
  POS: 'POS',
};

function normalizeCodePrefix(prefix, fallback = 'DOC') {
  const candidate = String(prefix || '').replace(/[^A-Za-z]/g, '').toUpperCase();
  if (!candidate) return fallback;
  return CODE_PREFIX_ALIASES[candidate] || candidate.slice(0, 4);
}

function extractCodePrefix(rawCode, fallback = 'DOC') {
  const raw = String(rawCode || '').trim().toUpperCase();
  if (!raw) return fallback;
  const match = raw.match(/^([A-Z]{2,5})[-/]?/);
  if (match) {
    return normalizeCodePrefix(match[1], fallback);
  }
  return fallback;
}

function codeNumericFragment(rawCode) {
  const raw = String(rawCode || '').trim();
  if (!raw) return '000000';

  const directMatch = raw.toUpperCase().match(/^[A-Z]{2,5}-(\d{6})$/);
  if (directMatch) return directMatch[1];

  const parts = raw.split(/[-/\s]+/).filter(Boolean);
  const tail = parts[parts.length - 1] || raw;
  const tailDigits = tail.replace(/\D/g, '');
  if (tailDigits) return tailDigits.slice(-6).padStart(6, '0');

  const allDigits = raw.replace(/\D/g, '');
  if (allDigits) return allDigits.slice(-6).padStart(6, '0');

  let checksum = 0;
  for (let index = 0; index < raw.length; index += 1) {
    checksum = ((checksum << 5) - checksum + raw.charCodeAt(index)) >>> 0;
  }
  return String(checksum % 1000000).padStart(6, '0');
}

export function formatDisplayCode(rawCode, fallbackPrefix = 'DOC') {
  const raw = String(rawCode || '').trim();
  if (!raw) return '';
  const prefix = extractCodePrefix(raw, normalizeCodePrefix(fallbackPrefix, fallbackPrefix));
  return `${prefix}-${codeNumericFragment(raw)}`;
}

export function formatDisplayOnlyCode(rawCode, fallbackPrefix = 'DOC') {
  const raw = String(rawCode || '').trim();
  if (!raw) return '';
  const prefix = extractCodePrefix(raw, normalizeCodePrefix(fallbackPrefix, fallbackPrefix));
  return `${prefix}${codeNumericFragment(raw)}`;
}
